/**
 * PortalEffect — Canvas particle system that lives inside the arch frame.
 *
 * Gold motes drift through the threshold between worlds.
 * Each icon gets its own preset — same frame, different soul.
 */

import { useRef, useEffect, useCallback } from 'react';
import type { PortalEffectConfig, Particle } from '../effects/types';

function drawTriangle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rotation: number) {
  ctx.beginPath();
  for (let i = 0; i < 3; i++) {
    const angle = rotation + (i * Math.PI * 2) / 3 - Math.PI / 2;
    const px = x + r * Math.cos(angle);
    const py = y + r * Math.sin(angle);
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
}

function drawHexagon(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rotation: number) {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = rotation + (i * Math.PI * 2) / 6;
    const px = x + r * Math.cos(angle);
    const py = y + r * Math.sin(angle);
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
}

interface PortalEffectProps {
  width: number;
  height: number;
  config: PortalEffectConfig;
  className?: string;
  style?: React.CSSProperties;
}

export function PortalEffect({ width, height, config, className, style }: PortalEffectProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animIdRef = useRef<number | null>(null);
  const prevTimeRef = useRef(0);

  const spawnParticle = useCallback(
    (cfg: PortalEffectConfig['particles']): Particle => {
      const lifetime = cfg.lifetime * (0.6 + Math.random() * 0.8);
      return {
        x: Math.random() * width,
        y: cfg.direction === 'up' ? height + Math.random() * 20 : -Math.random() * 20,
        size: cfg.minSize + Math.random() * (cfg.maxSize - cfg.minSize),
        alpha: cfg.minAlpha + Math.random() * (cfg.maxAlpha - cfg.minAlpha),
        speed: cfg.speed * (0.5 + Math.random()),
        drift: cfg.drift * (0.5 + Math.random()),
        driftPhase: Math.random() * Math.PI * 2,
        age: 0,
        lifetime,
      };
    },
    [width, height],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d')!;
    const dpr = Math.min(window.devicePixelRatio, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Init particles
    const pcfg = config.particles;
    particlesRef.current = Array.from({ length: pcfg.count }, () => {
      const p = spawnParticle(pcfg);
      p.age = Math.random() * p.lifetime; // stagger
      p.y = Math.random() * height; // scatter
      return p;
    });

    const loop = (ts: number) => {
      const dt = Math.min((ts - prevTimeRef.current) / 1000, 0.1);
      prevTimeRef.current = ts;

      ctx.clearRect(0, 0, width, height);

      // Edge glow
      if (config.edgeGlow.enabled) {
        const eg = config.edgeGlow;
        const pulse = 0.7 + 0.3 * Math.sin(ts * 0.001 * eg.breathe);
        const { r, g, b, a } = eg.color;
        const grad = ctx.createLinearGradient(0, 0, eg.width, 0);
        grad.addColorStop(0, `rgba(${r},${g},${b},${a * pulse})`);
        grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, eg.width, height);

        const gradR = ctx.createLinearGradient(width, 0, width - eg.width, 0);
        gradR.addColorStop(0, `rgba(${r},${g},${b},${a * pulse})`);
        gradR.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = gradR;
        ctx.fillRect(width - eg.width, 0, eg.width, height);
      }

      // Particles
      const particles = particlesRef.current;
      const { r, g, b } = pcfg.color;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.age += dt;

        if (p.age >= p.lifetime) {
          particles[i] = spawnParticle(pcfg);
          continue;
        }

        // Movement
        const progress = p.age / p.lifetime;
        switch (pcfg.direction) {
          case 'up':
            p.y -= p.speed * dt;
            p.x += Math.sin(p.age * 0.5 + p.driftPhase) * p.drift * dt;
            break;
          case 'down':
            p.y += p.speed * dt;
            p.x += Math.sin(p.age * 0.5 + p.driftPhase) * p.drift * dt;
            break;
          case 'radial': {
            const cx = width / 2, cy = height / 2;
            const angle = p.driftPhase + p.age * 0.2;
            const radius = progress * Math.max(width, height) * 0.5;
            p.x = cx + Math.cos(angle) * radius;
            p.y = cy + Math.sin(angle) * radius;
            break;
          }
          case 'spiral': {
            const cx2 = width / 2, cy2 = height / 2;
            const a = p.driftPhase + p.age * 0.8;
            const rad = 20 + progress * Math.max(width, height) * 0.4;
            p.x = cx2 + Math.cos(a) * rad;
            p.y = cy2 + Math.sin(a) * rad - p.speed * dt * 0.5;
            break;
          }
        }

        // Fade envelope
        let fade = 1;
        if (progress < pcfg.fadeIn) {
          fade = progress / pcfg.fadeIn;
        } else if (progress > 1 - pcfg.fadeOut) {
          fade = (1 - progress) / pcfg.fadeOut;
        }

        const finalAlpha = p.alpha * fade;
        if (finalAlpha <= 0) continue;

        ctx.fillStyle = `rgba(${r},${g},${b},${finalAlpha})`;
        // Alternate between triangles and hexagons
        if (i % 2 === 0) {
          drawTriangle(ctx, p.x, p.y, p.size * 1.6, p.driftPhase + p.age * 0.3);
        } else {
          drawHexagon(ctx, p.x, p.y, p.size * 1.3, p.driftPhase + p.age * 0.15);
        }
      }

      animIdRef.current = requestAnimationFrame(loop);
    };

    animIdRef.current = requestAnimationFrame(loop);

    return () => {
      if (animIdRef.current !== null) cancelAnimationFrame(animIdRef.current);
    };
  }, [width, height, config, spawnParticle]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        width,
        height,
        pointerEvents: 'none',
        ...style,
      }}
    />
  );
}

export default PortalEffect;
